'use client';

import React, { useEffect, useState } from 'react';
import { sendEmailVerification } from 'firebase/auth';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';

import { paths } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { firebaseClientAuth } from '@/src/lib/firebase/clientApp';
import { toast } from 'src/components/snackbar';

import { EmailInboxIcon } from 'src/assets/icons';

import { FormHead } from '../../components/form-head';
import { FormReturnLink } from '../../components/form-return-link';
import { useAuthContext } from 'src/auth/hooks';
import { signOut } from '../../context/firebase';

// ----------------------------------------------------------------------

export function VerifieraView() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, authenticated, loading, checkUserSession } = useAuthContext();

  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'info' | 'success' | 'error'; message: string } | null>(null);

  const queryEmail = searchParams.get('email');
  const userEmail = user?.email || queryEmail || '';

  useEffect(() => {
    const emailVerified = user?.emailVerified;
    if (!loading && authenticated && emailVerified) {
      router.replace(paths.ONBOARDING);
    }
  }, [loading, authenticated, user, router]);

  /**
   * E-postverifiering är Firebases, och i lokalt utvecklingsläge finns ingen Firebase alls —
   * `firebaseClientAuth` är null (se `lib/firebase/clientApp.ts`). Knapparna läste tidigare
   * `.currentUser` rakt av och gav "Cannot read properties of null". Det finns ingenting att
   * verifiera i det läget, så vi säger det och visar vägen vidare i stället.
   */
  const visaAttVerifieringSaknas = () => {
    setFeedback({
      type: 'info',
      message:
        'Den här miljön använder lokal inloggning utan e-postverifiering. Det finns inget mejl '
        + 'att vänta på — gå vidare till appen.',
    });
  };

  const handleCheckVerification = async () => {
    setIsChecking(true);
    setFeedback(null);
    try {
      if (!firebaseClientAuth) {
        visaAttVerifieringSaknas();
        return;
      }
      if (firebaseClientAuth.currentUser) {
        await firebaseClientAuth.currentUser.reload();
        if (firebaseClientAuth.currentUser.emailVerified) {
          // Force a token refresh to update the global auth context
          await checkUserSession?.();
          toast.success('E-postadressen är verifierad!');
          router.replace(paths.ONBOARDING);
          return;
        }
        setFeedback({
          type: 'info',
          message: 'E-postadressen är inte verifierad ännu. Klicka på länken i mailet som vi skickade till dig.',
        });
      } else {
        router.replace(paths.LOGGA_IN);
      }
    } catch (error: any) {
      console.error(error);
      setFeedback({
        type: 'error',
        message: error?.message || 'Ett fel uppstod vid kontroll av verifiering.',
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    setFeedback(null);
    try {
      if (!firebaseClientAuth) {
        visaAttVerifieringSaknas();
        return;
      }
      const currentUser = firebaseClientAuth.currentUser;
      if (currentUser) {
        const actionCodeSettings = {
          url: `${window.location.origin}${paths.authAction}`,
          handleCodeInApp: true,
        };
        await sendEmailVerification(currentUser, actionCodeSettings);
        setFeedback({
          type: 'success',
          message: 'Ett nytt verifieringsmail har skickats till din e-postadress.',
        });
        toast.success('Nytt verifieringsmail skickat!');
      } else {
        router.replace(paths.LOGGA_IN);
      }
    } catch (error: any) {
      console.error(error);
      setFeedback({
        type: 'error',
        message: error?.message || 'Kunde inte skicka verifieringsmail. Försök igen om en stund.',
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleReturnToSignIn = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await signOut();
    } catch (error) {
      console.error('Kunde inte logga ut:', error);
    }
    router.replace(paths.LOGGA_IN);
  };

  return (
    <>
      <FormHead
        icon={<EmailInboxIcon />}
        title="Bekräfta din e-postadress"
        description={
          <>
            {`Vi har skickat en verifieringslänk till `}
            <strong>{userEmail || 'din e-postadress'}</strong>.
            <br />
            {`Vänligen klicka på länken för att aktivera ditt konto.`}
          </>
        }
      />

      {feedback && (
        <Alert severity={feedback.type} sx={{ mt: 3, mb: 1 }}>
          {feedback.message}
        </Alert>
      )}

      <Box sx={{ gap: 1.5, display: 'flex', flexDirection: 'column', mt: 3, mb: 2 }}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          loading={isChecking}
          loadingIndicator="Kontrollerar..."
          onClick={handleCheckVerification}
        >
          Jag har bekräftat min e-post
        </Button>

        <Button
          variant="outlined"
          size="large"
          fullWidth
          color="inherit"
          loading={isResending}
          loadingIndicator="Skickar..."
          onClick={handleResendEmail}
        >
          Skicka verifieringslänk igen
        </Button>
      </Box>

      <FormReturnLink
        href={paths.LOGGA_IN}
        label="Tillbaka till inloggning"
        onClick={handleReturnToSignIn}
        sx={{ mt: 1, cursor: 'pointer' }}
      />
    </>
  );
}
